import axios, { AxiosRequestConfig, AxiosResponse } from "axios"
import { createContext, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import Cookies from 'universal-cookie'
import { APIEndpoints } from "../constants/api-endpoints"
import { LoginApiPayload } from "../interfaces/api-request"
import { ChangePasswordApiPayload } from "../interfaces/api-request"
import { getKey } from "../services/util"
import { HttpBaseRequest } from "../services/_http"
import { mapBackendPermissions } from "constants/permission.mapper"
interface AuthProviderProps {
    children: any
}
interface AuthContextInterface {
    user: UserSession | null
    updateUserSession: (sessionData: UserSession) => void
    logout: Function
    changePassword: (credentials: ChangePasswordApiPayload, onSuccess?: (responseData?: any) => void, onError?: (errorResponse?: AxiosResponse) => void) => void
    login: (credentials: LoginApiPayload, onSuccess?: (responseData?: any) => void, onError?: (errorResponse?: AxiosResponse) => void) => void
    isAllowedRouteKey: (key?: string) => boolean
    setDarkTheme: (enabled: boolean) => void
    darkTheme: boolean
}

interface UserSession {
    accessToken: string
    refreshToken: string
    permissions?: any
    username?: string
    role?: string
    email?: string
}

const AuthContext = createContext<AuthContextInterface | null>(null)
const cookies = new Cookies()

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<UserSession | null>(cookies.get('_cuser') ?? null)
    const [darkTheme, changeDarkTheme] = useState<boolean>(!!localStorage?.getItem('darktheme'))
    const navigate = useNavigate()

    const login = (loginCredentials: LoginApiPayload, onSuccess?: (responseData?: any) => void, onError?: (errorResponse?: AxiosResponse) => void) => {
        const method = 'POST'
        return HttpBaseRequest({
            url: APIEndpoints.Auth.Login,
            method: method,
            data: loginCredentials
        }).then((response) => {
            if (response?.data) {
     
         const frontendPermissions = mapBackendPermissions(response.data?.permissions ?? []);
           const finalData = {...response.data, permissions: frontendPermissions};
                updateUserSession(finalData);
                onSuccess && onSuccess(finalData)
            }
        }).catch(error => {
            onError && onError(error)
        })
    }


    const changePassword = (changePassworddata: ChangePasswordApiPayload, onSuccess?: (responseData?: any) => void, onError?: (errorResponse?: AxiosResponse) => void) => {
        const method = 'PUT'
        return HttpBaseRequest({
            url: APIEndpoints.Auth.ChangePassword,
            method: method,
            data: changePassworddata,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user?.accessToken}`
            }
        }).then((response) => {
            if (response?.data) {
                onSuccess && onSuccess(response.data)
            }
        }).catch(error => {
            onError && onError(error)
        })
    }

    const updateUserSession = (sessionData: UserSession) => {
        let user_data = sessionData
        cookies.set('_cuser', user_data, { path: '/' })
        setUser(user_data)
    }

    const logout = (redirectPath?: string) => {
        cookies.remove('_cuser', {
            path: '/'
        })
        navigate(`/login${redirectPath ? `?redirectTo=${redirectPath}` : ''}`, { replace: true })
        setUser(null)
    }

    const isAllowedRouteKey = (key?: string) => {
        return !!key && getKey(key, user?.permissions)
    }

    const setDarkTheme = (enabled: boolean) => {
        changeDarkTheme(enabled)
        enabled ? localStorage.setItem('darktheme', 'true') : localStorage.removeItem('darktheme')
    }

    const initialContext: AuthContextInterface = { user, updateUserSession, logout, changePassword, isAllowedRouteKey, setDarkTheme, darkTheme, login }
    return (
        <AuthContext.Provider value={initialContext}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    return useContext(AuthContext)
}